















import {
  Component, Prop, Watch,
} from 'vue-property-decorator';
import type {
  Unit, Onset, Pixel, Second, Point,
} from '@/data';
import { PixelUnitWithSeconds } from '@/data';
import type { Image3DPageData } from '@/data/Source';
import type { DeMuxPixel } from '@/data/PixelUtils';
import PixelUtils from '@/data/PixelUtils';
import type { Emitter, Registerer } from '../LabelBehavior.vue';
import LabelBehavior from '../LabelBehavior.vue';

/**
 * The type of the cursorMove public event handler
 *
 * Allows any component to ask for a label in view
 */
type CursorMove = (position: Point) => void;

/**
 * Extension of the Registerer type
 *
 * Allows any behavior to subscribe to the public event 'cursor-move'
 */
export type BCursorMoveRegisterer = Registerer &
((name: 'cursor-move', handler: CursorMove) => void);

/**
 * Emitter type, to wrap LabelCanvas.$emit
 *
 * Allows BCanvasCursor and BCanvasMeasureCursor to emit 'cursor-move' events
 */
export type BCursorMoveEmitter = Emitter &
((name: 'cursor-move', payload: Point) => void);

@Component
export default class BCanvasCursor extends LabelBehavior<BCursorMoveEmitter> {
  @Prop({ required: true })
  musicalTime!: Onset | null;

  @Prop({ default: null })
  realTime! : Second | null;

  @Prop({ required: true })
  pixelUnit!: Unit<Pixel>;

  @Prop()
  pages!: Array<Image3DPageData>;

  @Prop({ default: false })
  byMeasure!: boolean;

  @Prop({ default: false })
  focusCursor!: boolean;

  /**
   * Used to prevent using seconds directly if currently on the wrong track
   * Just a safety check for a very specific corner case:
   *  * playing track A
   *  * canvas uses a spectrogram from track B
   *  * track B and A have different synchro files
   */
  @Prop({ default: false })
  isDifferentTrack!: boolean;

  @Prop({ default: false })
  inMeasureCursor!: boolean;

  // @Prop({ default: 30 })
  height: Pixel = 0 as Pixel;

  y = 0;

  width: Pixel = 4 as Pixel;

  position: DeMuxPixel | null = null;

  @Watch('realTime')
  @Watch('musicalTime')
  @Watch('pixelUnit')
  onTimeChange() {
    const oldPosition = this.position;
    this.position = this.calculatePosition();
    const currentRowY = this.pages[this.position?.page || 0].rows[this.position?.row || 0].y;
    const currentPageY = this.pages[this.position?.page || 0].height * (this.position?.page || 0);
    this.y = currentPageY + currentRowY;
    if (this.inMeasureCursor) {
      this.height = 20 as Pixel;
    } else {
      this.height = this.pages[this.position?.page || 0].rows[this.position?.row || 0].height;
    }

    if (this.canEmitOrRegister
      && this.position !== null
      && this.position !== oldPosition) {
      this.emit(
        'cursor-move',
        { x: this.position.x, y: this.y } as Point,
      );
    }
  }

  calculatePosition(): DeMuxPixel | null {
    // If on a spectrogram of current track, load position from seconds directly
    if (
      this.realTime !== null
      && !this.isDifferentTrack
      && PixelUnitWithSeconds.isUnitWithSeconds(this.pixelUnit)) {
      // Might be best to forbid loading the spectrogram of the wrong audio file
      return PixelUtils.deMux(this.pixelUnit.fromSecondToPixel(this.realTime));
    }
    // If not a spectrogram, load pos from onset
    if (this.musicalTime !== null) {
      return PixelUtils.deMux(this.pixelUnit.fromOnset(this.musicalTime).value);
    }
    // Otherwise don't display cursor
    return null;
  }
}
